/**
 * NetworkUndoRedoHandler - Manejador de atajos de teclado para undo/redo de red
 *
 * @version 1.0
 * @created 2026-03-06
 * @module core/spatial/components
 *
 * @description
 * Maneja los atajos de teclado globales para undo/redo:
 * - Ctrl+Z / Cmd+Z: Deshacer
 * - Ctrl+Y / Cmd+Shift+Z: Rehacer
 *
 * @example
 * ```tsx
 * <NetworkUndoRedoHandler />
 * ```
 */

import { useEffect } from 'react';
import { useNetworkEdit } from '../contexts/NetworkEditContext';

/**
 * Manejador de atajos de teclado para undo/redo
 */
export function NetworkUndoRedoHandler(): null {
  const { undo, redo, canUndo, canRedo } = useNetworkEdit();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      // Detectar si es Mac o Windows/Linux
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      // Ignorar si está en un input/textarea
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Ctrl+Z / Cmd+Z: Undo
      if (modifier && event.key === 'z' && !event.shiftKey) {
        if (canUndo()) {
          event.preventDefault();
          console.log('[NetworkUndoRedoHandler] Undo triggered by keyboard');
          void undo();
        }
        return;
      }

      // Ctrl+Y / Cmd+Shift+Z: Redo
      if (
        (modifier && event.key === 'y' && !isMac) || // Ctrl+Y en Windows/Linux
        (modifier && event.key === 'z' && event.shiftKey && isMac) // Cmd+Shift+Z en Mac
      ) {
        if (canRedo()) {
          event.preventDefault();
          console.log('[NetworkUndoRedoHandler] Redo triggered by keyboard');
          void redo();
        }
        return;
      }
    };

    // Agregar listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, canUndo, canRedo]);

  // Este componente no renderiza nada
  return null;
}
